/**
 * Printer Service - Abstraction Layer
 *
 * This module provides a unified interface for printer operations.
 * The actual implementation can be swapped out without changing
 * any code that depends on this service.
 *
 * To switch from QZ Tray to a custom implementation:
 * 1. Create a new implementation file (e.g., custom-printer.service.js)
 * 2. Change the import below to point to your new implementation
 * 3. Ensure your implementation exports the same interface
 *
 * REQUIRED INTERFACE:
 * - getDefaultZplPrinter(): string | null
 * - getDefaultPdfPrinter(): string | null
 * - setDefaultZplPrinter(printerName: string): void
 * - setDefaultPdfPrinter(printerName: string): void
 * - connectToService(options?): Promise<boolean>
 * - disconnectFromService(): Promise<void>
 * - isConnected(): boolean
 * - findPrinters(useCache?): Promise<string[]>
 * - findPrinterSummaries(useCache?): Promise<Object[]>
 * - printZpl(zplData, printerName?): Promise<void>
 * - printPdf(pdfData | pdfData[], printerName?, options?): Promise<void>
 */

// ============================================================================
// IMPLEMENTATION SELECTION
// ============================================================================
// TO SWITCH IMPLEMENTATIONS: Change this import to your custom service
import * as qzTrayService from "./qz-tray.service.js";

// ============================================================================
// ABSTRACTED INTERFACE
// ============================================================================

/**
 * Get the configured default ZPL printer
 * @returns {string|null} Printer name or null if not configured
 */
export function getDefaultZplPrinter() {
    return qzTrayService.getDefaultZplPrinter();
}

/**
 * Get the configured default PDF printer
 * @returns {string|null} Printer name or null if not configured
 */
export function getDefaultPdfPrinter() {
    return qzTrayService.getDefaultPdfPrinter();
}

/**
 * Set the default ZPL printer
 * @param {string} printerName - Name of the printer
 */
export function setDefaultZplPrinter(printerName) {
    return qzTrayService.setDefaultZplPrinter(printerName);
}

/**
 * Set the default PDF printer
 * @param {string} printerName - Name of the printer
 */
export function setDefaultPdfPrinter(printerName) {
    return qzTrayService.setDefaultPdfPrinter(printerName);
}

/**
 * Clear the default ZPL printer
 */
export function clearDefaultZplPrinter() {
    return qzTrayService.clearDefaultZplPrinter();
}

/**
 * Clear the default PDF printer
 */
export function clearDefaultPdfPrinter() {
    return qzTrayService.clearDefaultPdfPrinter();
}

/**
 * Connect to the printer service
 * @param {Object} options - Connection options
 * @returns {Promise<boolean>} True if connected successfully
 */
export async function connectToService(options = {}) {
    return qzTrayService.connectToQZ(options);
}

/**
 * Disconnect from the printer service
 * @param {boolean} force - If true, attempts disconnect even if not connected
 * @returns {Promise<void>}
 */
export async function disconnectFromService(force = false) {
    return qzTrayService.disconnectFromQZ(force);
}

/**
 * Check if currently connected to the printer service
 * @returns {boolean}
 */
export function isConnected() {
    return qzTrayService.isConnected();
}

/**
 * Whether the printer service connected at least once this session
 * @returns {boolean}
 */
export function hasConnectedBefore() {
    return qzTrayService.hasConnectedBefore();
}

/**
 * Find all available printers
 * @param {boolean} useCache - Whether to use cached results
 * @returns {Promise<string[]>} Array of printer names
 */
export async function findPrinters(useCache = true) {
    return qzTrayService.findPrinters(useCache);
}

/**
 * Find all available printers with lightweight capability hints.
 * Uses printer names only, which is fast enough for dropdown selection screens.
 * @param {boolean} useCache - Whether to use cached results
 * @returns {Promise<Object[]>} Array of printer summaries
 */
export async function findPrinterSummaries(useCache = true) {
    return qzTrayService.findPrinterSummaries(useCache);
}

/**
 * Find all available printers with detailed capabilities
 * @param {boolean} useCache - Whether to use cached results
 * @returns {Promise<Object[]>} Array of printer objects with name, driver, and capabilities
 */
export async function findPrintersWithDetails(useCache = true) {
    return qzTrayService.findPrintersWithDetails(useCache);
}

/**
 * Find a specific printer by name
 * @param {string} printerName - Name of the printer to find
 * @returns {Promise<string|null>} Printer name if found, null otherwise
 */
export async function findSpecificPrinter(printerName) {
    return qzTrayService.findSpecificPrinter(printerName);
}

/**
 * Print ZPL data to a thermal printer
 * @param {string} zplData - Raw ZPL commands
 * @param {string|null} printerName - Optional printer name (uses default if not specified)
 * @returns {Promise<void>}
 */
export async function printZpl(zplData, printerName = null) {
    return qzTrayService.printZpl(zplData, printerName);
}

/**
 * Print PDF data to a printer
 * Supports bulk printing - pass an array of PDFs for optimized batch printing
 * @param {Blob|Blob[]|ArrayBuffer|ArrayBuffer[]|string|string[]} pdfData - PDF data to print (single or array)
 * @param {string|null} printerName - Optional printer name (uses default if not specified)
 * @param {Object} options - Printing options
 * @returns {Promise<void>}
 */
export async function printPdf(pdfData, printerName = null, options = {}) {
    return qzTrayService.printPdf(pdfData, printerName, options);
}

/**
 * Wait for an in-progress reconnect to finish (never starts a new one).
 * Returns true if connected after waiting, false otherwise.
 * Browser Print-only users get false instantly with zero delay.
 * @param {number} timeoutMs - Max time to wait (default 3000ms)
 * @returns {Promise<boolean>}
 */
export async function ensureConnected(timeoutMs = 3000) {
    return qzTrayService.ensureConnected(timeoutMs);
}

/**
 * Get installation/setup instructions for the printer service
 * @returns {Object} Instructions object with message and link
 */
export function getInstallationInstructions() {
    return qzTrayService.getInstallationInstructions();
}

/**
 * Initialize the printer service (auto-connect if enabled)
 * @returns {Promise<boolean>}
 */
export async function initializePrinterService() {
    return qzTrayService.initializeQzTray();
}

// ============================================================================
// SERVICE METADATA
// ============================================================================

/**
 * Get information about the current printer service implementation
 * @returns {Object} Service metadata
 */
export function getServiceInfo() {
    return {
        name: "QZ Tray",
        version: "2.x",
        implementation: "qz-tray.service.js",
        // When you create a custom implementation, update these values
        canBeReplaced: true,
        replacementNotes:
            "To replace this service, implement the required interface and update the import in printer.service.js",
    };
}
